import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { FetchSalesPersonReport_api, FetchSalesPersons_api } from "../../Api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Download, SlidersHorizontal, RotateCcw, Users } from "lucide-react";
import { Spin, DatePicker, Select, Drawer, Space, Button as AntdButton } from "antd";
import dayjs from "dayjs";
import { downloadExcel } from "../../lib/excelGenerator";

const { RangePicker } = DatePicker;

const toNumber = (value: any): number =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-LK", {
    style: "currency",
    currency: "LKR",
    minimumFractionDigits: 2,
  }).format(value);

export const ReportBySalesPerson = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    AppFeature.REPORT_BY_SALES_PERSON,
    ["admin", "manager"],
  );

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [salesPersonId, setSalesPersonId] = useState<number | undefined>(undefined);
  const [hasApplied, setHasApplied] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<any>(null);
  const [draftSalesPersonId, setDraftSalesPersonId] = useState<number | undefined>(undefined);

  const [salesPersons, setSalesPersons] = useState<any[]>([]);

  useEffect(() => {
    if (hasPermission) {
      FetchSalesPersons_api(1, 1000, "").then((response: any) => {
        if (response && response.isDataSet) {
          setSalesPersons(response.data || []);
        }
      });
    }
  }, [hasPermission]);

  const fetchReport = async (start: string, end: string, spId?: number) => {
    if (!start || !end) {
      alert("Please select both start and end dates");
      return;
    }
    setLoading(true);
    const response = await FetchSalesPersonReport_api(
      start,
      end,
      spId ? String(spId) : "",
    );
    if (response.success) {
      setReportData(response.data || []);
      setTotalAmount(
        (response.data || []).reduce((acc: number, row: any) => acc + toNumber(row.amount), 0),
      );
    } else {
      alert(response.msg || "Failed to fetch report");
    }
    setLoading(false);
  };

  const openDrawer = () => {
    setDraftRange(startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null);
    setDraftSalesPersonId(salesPersonId);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    const s = draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "";
    const e = draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "";
    setStartDate(s);
    setEndDate(e);
    setSalesPersonId(draftSalesPersonId);
    setHasApplied(true);
    setDrawerOpen(false);
    fetchReport(s, e, draftSalesPersonId);
  };

  const resetFilters = () => {
    setDraftRange(null);
    setDraftSalesPersonId(undefined);
    setStartDate("");
    setEndDate("");
    setSalesPersonId(undefined);
    setReportData([]);
    setTotalAmount(0);
    setHasApplied(false);
    setDrawerOpen(false);
  };

  const periodLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";
  const activeSalesPersonName =
    salesPersons.find((sp) => sp.id === salesPersonId)?.name || "";
  const filterSummary = [
    periodLabel,
    activeSalesPersonName || (salesPersonId ? "Selected Sales Person" : ""),
  ].filter(Boolean).join(" | ");

  const handleDownloadExcel = () => {
    const headers = [
      "Date",
      "Sales Person",
      "Inquiry #",
      "Vehicle Model",
      "Engine Number",
      "Chassis Number",
      "Customer",
      "Amount",
    ];
    const data = reportData.map((row) => [
      row.date || "N/A",
      row.sales_person_name || "N/A",
      row.inquiry_number || "N/A",
      row.vehicle_model || "N/A",
      row.engine_number || "N/A",
      row.chassis_number || "N/A",
      row.customer_name || "N/A",
      toNumber(row.amount),
    ]);
    downloadExcel(
      `Sales Person Report${filterSummary ? ` (${filterSummary})` : ""}`,
      headers,
      data,
      `Sales_Person_Report_${startDate}_${endDate}`,
      undefined,
      undefined,
      formatCurrency(totalAmount),
      `${reportData.length} Deals Closed`,
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Sales Report by Sales Person</h1>
        <div className="flex gap-3">
          <Button
            onClick={openDrawer}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
          >
            <SlidersHorizontal className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Filters</span>
          </Button>
          <Button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Users className="w-4 h-4 text-emerald-600" />
          <span>Filters: </span>
          <span className="font-medium text-gray-700">
            {filterSummary || "None applied — open Filters to pick a period"}
          </span>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex flex-wrap gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL SALES GENERATED</p>
                <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">DEALS CLOSED</p>
                <p className="text-2xl font-bold">{reportData.length}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">AVG PER DEAL</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(reportData.length > 0 ? totalAmount / reportData.length : 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Spin size="large" />
                </div>
              ) : (
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Sales Person</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Inquiry #</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Vehicle & Customer</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((row, index) => (
                      <TableRow
                        key={`${row.inquiry_number}-${index}`}
                        className="border-b last:border-0 hover:bg-gray-50"
                      >
                        <TableCell className="px-4 py-3">
                          {row.date ? String(row.date).split(" ")[0] : "N/A"}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="font-medium text-gray-800">{row.sales_person_name}</span>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {row.inquiry_number || "N/A"}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-700">
                              {row.vehicle_model} <span className="text-gray-400">({row.vehicle_type})</span>
                            </span>
                            <span className="text-xs text-gray-500">
                              Sold to: {row.customer_name || "N/A"}
                            </span>
                            <span className="text-[10px] text-gray-400 font-mono">
                              {row.engine_number} / {row.chassis_number}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">
                          {formatCurrency(toNumber(row.amount))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && reportData.length === 0 && hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No sales activity recorded for the applied period.</p>
          </div>
        </div>
      )}
      {!loading && reportData.length === 0 && !hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Open Filters and pick a period to view sales performance.</p>
          </div>
        </div>
      )}

      <Drawer
        title="Sales Report Filters"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <AntdButton
              onClick={resetFilters}
              className="flex items-center gap-2 border-gray-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </AntdButton>
            <AntdButton
              type="primary"
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Apply Filters
            </AntdButton>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Sales Person
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Sales Persons"
              value={draftSalesPersonId}
              onChange={(val: any) => setDraftSalesPersonId(val)}
              allowClear
              showSearch
              optionFilterProp="children"
              options={salesPersons.map((sp) => ({ value: sp.id, label: sp.name }))}
            />
          </div>
        </Space>
      </Drawer>
    </div>
  );
};